import React, { useState, useMemo, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Users from './components/Users';
import Challans from './components/Challans';
import Scholarships from './components/Scholarships';
import EditRecords from './components/EditRecords';
import AuditLogs from './components/AuditLogs';
import TeleChallan from './components/TeleChallan';
import DigiSindhPsids from './components/DigiSindhPsids';
import Courses from './components/Courses';
import Layout from './components/Layout';
import Applications from './components/Applications';
import Payments from './components/Payments';
import Reports from './components/Reports';
import UsersAndRoles from './components/UsersAndRoles';
import Settings from './components/Settings';

// Create Color Mode Context
export const ColorModeContext = createContext({ toggleColorMode: () => { } });

function App() {
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    const savedMode = localStorage.getItem('themeMode');
    return (savedMode as 'light' | 'dark') || 'light';
  });

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === 'light' ? 'dark' : 'light';
          localStorage.setItem('themeMode', newMode);
          return newMode;
        });
      },
    }),
    []
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: mode === 'light' ? '#1976d2' : '#90caf9',
          },
          background: {
            default: mode === 'light' ? '#f8fafc' : '#0f172a',
            paper: mode === 'light' ? '#ffffff' : '#1e293b',
          },
        },
        typography: {
          fontFamily: "'Plus Jakarta Sans', 'Outfit', sans-serif",
        },
        shape: {
          borderRadius: 12,
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                fontWeight: 600,
              },
            },
          },
        },
      }),
    [mode]
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="users" element={<Users />} />
                <Route path="challans" element={<Challans />} />
                <Route path="scholarships" element={<Scholarships />} />
                <Route path="edit" element={<EditRecords />} />
                <Route path="audit-logs" element={<AuditLogs />} />
                <Route path="tele-challan" element={<TeleChallan />} />
                <Route path="digisindh-psids" element={<DigiSindhPsids />} />
                <Route path="courses" element={<Courses />} />
                <Route path="applications" element={<Applications />} />
                <Route path="payments" element={<Payments />} />
                <Route path="reports" element={<Reports />} />
                <Route path="roles" element={<UsersAndRoles />} />
                <Route path="settings" element={<Settings />} />
              </Route>
            </Routes>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
